<template>
  <div class="mt-component-form">
    <!-- Desktop Header -->
    <vs-row class="my-4 center-flex desktop-layout">
      <h5><i class="dot"></i>{{ title }}</h5>
      <div class="ml-auto flex items-center">
        <AIButton
          v-if="!isPrint"
          :label="$t('이미지로 AI 추천')"
          icon="ImageIcon"
          theme="outline"
          :fullWidth="false"
          @click="handleImageAIRecommendation"
        />
        <AIButton
          v-if="!isPrint"
          :label="$t('주요 유해·위험요인 AI 추천')"
          icon="ZapIcon"
          theme="outline"
          class="ml-4"
          :fullWidth="false"
          padding="8px 20px"
          :disabled="!aiRecommendationEnabled"
          tooltip="AI가 주요 유해·위험요인을 추천합니다"
          @click="handleAIRecommendation"
        />
        <vs-button v-if="!isPrint" class="ml-4" color="secondary" @click="handleAddRow">{{$t('항목추가')}} </vs-button>
        <vs-button v-if="!isPrint" class="ml-4" color="secondary" type="border" :disabled="selectedItemsCount === 0" @click="handleRemoveRow">{{$t('선택삭제')}} </vs-button>
      </div>
    </vs-row>

    <!-- Mobile Header -->
    <div class="mobile-layout mobile-title-header">
      <div class="mobile-title-content">
        <h5 class="mobile-title"><i class="dot"></i>{{ title }}</h5>
        <div v-if="!isPrint" class="mobile-ai-buttons">
          <AIButton
            :label="$t('이미지로 AI 추천')"
            icon="ImageIcon"
            theme="outline"
            :fullWidth="true"
            @click="handleImageAIRecommendation"
          />
          <AIButton
            :label="$t('주요 유해·위험요인 AI 추천')"
            icon="ZapIcon"
            theme="outline"
            :fullWidth="true"
            :disabled="!aiRecommendationEnabled"
            :tooltip="$t('AI가 주요 유해·위험요인을 추천합니다')"
            @click="handleAIRecommendation"
          />
        </div>
      </div>
    </div>

    <!-- Desktop Layout -->
    <div class="table-container desktop-layout">
      <div class="overflow-x-auto">
        <table class="risk-appraisal-table">
          <thead>
            <tr class="table-header">
              <th class="table-header-cell checkbox-cell">
                <vs-checkbox @change="handleCheckAll" :value="allChecked"></vs-checkbox>
              </th>
              <th v-if="isNumbering" class="table-header-cell numbering-cell">No.</th>
              <th class="table-header-cell">{{$t('세부작업명')}}</th>
              <th class="table-header-cell">{{$t('평가구분')}} /<br />{{$t('유해·위험요인')}} /<br />{{$t('재해형태')}}</th>
              <th class="table-header-cell">{{$t('현재 안전보건조치')}}</th>
              <th class="table-header-cell risk-cell">{{$t('현재 위험도')}}</th>
              <th class="table-header-cell">{{$t('위험감소대책')}}</th>
              <th class="table-header-cell risk-cell">{{$t('개선후 위험도')}}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0" class="empty-row">
              <td :colspan="isNumbering ? 8 : 7" class="empty-cell">
                <div class="center-flex">
                  <feather-icon icon="InfoIcon" class="mr-2" style="width: 18px; height: 18px" />{{$t('s항목이 존재하지 않습니다.', { s: title })}}
                </div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="(row, rowIndex) in list" :key="row.id" class="table-row">
                <td class="table-cell checkbox-cell">
                  <vs-checkbox v-model="row.input_check" :readonly="readonly" />
                </td>
                <td v-if="isNumbering" class="table-cell numbering-cell">
                  {{ rowIndex + 1 }}
                </td>
                <td class="table-cell">
                  <p v-if="isPrint">
                    {{ row.input_1 }}
                  </p>
                  <ml-textarea v-else class="m-0" :readonly="readonly" rows="7" v-model="row.input_1" @input="emitListChange()"/>
                </td>
                <td class="table-cell">
                  <div v-if="!isPrint" class="mb-2">
                    <AIButton
                      label="AI 분석"
                      icon="ZapIcon"
                      :disabled="!row.input_1"
                      :loading="aiAnalyzingState[row.id]"
                      loading-text="분석중..."
                      tooltip="세부작업명을 기반으로 평가구분, 유해·위험요인, 재해형태를 AI가 자동 분석합니다"
                      @click="handleAIAnalyze(rowIndex)"
                    />
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">{{$t('평가구분')}}</label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_2 }}
                    </p>
                    <check-type-a-selector v-else v-model="row.input_2" :readonly="readonly || aiAnalyzingState[row.id]" class="w-full" @change="handleTypeInput(rowIndex)" />
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">{{$t('유해·위험요인')}}</label>
                    <p v-if="isPrint">
                      {{ row.input_4 }}
                    </p>
                    <vs-select v-else class="w-full" autocomplete v-model="row.input_4" :disabled="readonly || aiAnalyzingState[row.id]">
                      <vs-select-item :key="index" :value="item.value" :text="$t(item.text)" v-for="(item, index) in handleAppraisalType(row.input_2)" />
                    </vs-select>
                  </div>
                  <div class="mb-3">
                    <label class="text-xs text-gray-600 mb-1 block">{{$t('재해형태')}}</label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_3 }}
                    </p>
                    <disaster-class-selector v-else class="w-full" v-model="row.input_3" :disabled="aiAnalyzingState[row.id]" />
                  </div>
                </td>
                <td class="table-cell">
                  <div v-if="!isPrint" class="mb-2">
                    <AIButton
                      label="AI 추천"
                      icon="ShieldIcon"
                      :disabled="!row.input_1"
                      :loading="safetyMeasuresAnalyzingState[row.id]"
                      loading-text="분석중..."
                      tooltip="현재 상황에 적합한 안전보건조치를 AI가 추천합니다"
                      @click="handleAISafetyMeasures(rowIndex)"
                    />
                  </div>
                  <p v-if="isPrint">
                    {{ row.input_5 }}
                  </p>
                  <ml-textarea v-else class="m-0" :readonly="readonly" rows="7" v-model="row.input_5" @input="emitListChange()"/>
                </td>
                <td class="table-cell risk-cell">
                  <div v-if="!isPrint" class="mb-2">
                    <AIButton
                      label="AI 분석"
                      icon="TrendingUpIcon"
                      :disabled="!row.input_1"
                      :loading="currentRiskAnalyzingState[row.id]"
                      loading-text="분석중..."
                      tooltip="현재 상황의 위험성 가능성과 중대성을 AI가 분석합니다"
                      @click="handleAICurrentRiskAnalyze(rowIndex)"
                    />
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('가능성')}}
                      <vx-tooltip :title="$t('가능성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_6 }}
                    </p>
                    <vs-select v-else class="w-full" autocomplete :disabled="readonly || currentRiskAnalyzingState[row.id]" v-model="row.input_6" @change="handleChangeinput(row.id, row.input_6, row.input_7, 'input_8')">
                      <vs-select-item value="" text="-" />
                      <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber1" />
                    </vs-select>
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('중대성')}}
                      <vx-tooltip :title="$t('중대성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip2()" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_7 }}
                    </p>
                    <vs-select v-else class="w-full" autocomplete :disabled="readonly || currentRiskAnalyzingState[row.id]" v-model="row.input_7" @change="handleChangeinput(row.id, row.input_6, row.input_7, 'input_8')">
                      <vs-select-item value="" text="-" />
                      <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber2" />
                    </vs-select>
                  </div>
                  <div>
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('위험도')}}
                      <vx-tooltip :title="$t('위험도이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip3()" />
                      </vx-tooltip>
                      <!-- AI 위험도 분석 결과 툴팁 -->
                      <vx-tooltip
                        v-if="row.riskAnalysis && row.riskAnalysis.평가근거 && !isPrint"
                        :title="$t('AI 분석 결과')"
                        :html="$t('AI가 분석한 위험도 평가근거를<br/>확인하려면 클릭하세요')"
                        style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center"
                        position="top"
                        delay=".1s"
                      >
                        <feather-icon icon="ZapIcon" style="cursor: pointer; width: 16px; color: #667eea" @click="showRiskAnalysisDialog(rowIndex)" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_8 ? `${$t(getResultRisk(row.input_8))} (${row.input_6 * row.input_7})` : "" }}
                    </p>
                    <ml-input v-else :readonly="true" class="w-full" v-text="`${row.input_8 ? `${$t(getResultRisk(row.input_8))} (${row.input_6 * row.input_7})` : ''}`" :style="{ color: row.input_8_color }" />
                  </div>
                </td>
                <td class="table-cell">
                  <div v-if="!isPrint" class="mb-2">
                    <AIButton
                      label="AI 추천"
                      icon="ShieldIcon"
                      :disabled="!row.input_1"
                      :loading="riskReductionsAnalyzingState[row.id]"
                      loading-text="분석중..."
                      tooltip="위험을 감소시킬 수 있는 대책을 AI가 추천합니다"
                      @click="handleAIRiskReductions(rowIndex)"
                    />
                  </div>
                  <p v-if="isPrint">
                    {{ row.input_9 }}
                  </p>
                  <ml-textarea v-else class="m-0" :readonly="readonly || riskReductionsAnalyzingState[row.id]" rows="7" v-model="row.input_9" @input="emitListChange()"/>
                </td>
                <td class="table-cell risk-cell">
                  <div v-if="!isPrint" class="mb-2">
                    <AIButton
                      label="AI 분석"
                      icon="TrendingUpIcon"
                      :disabled="!row.input_1 || !row.input_9"
                      :loading="afterRiskAnalyzingState[row.id]"
                      loading-text="분석중..."
                      tooltip="위험감소대책 적용 후의 개선된 위험성을 AI가 분석합니다"
                      @click="handleAIAfterRiskAnalyze(rowIndex)"
                    />
                  </div>
                  <div v-if="!hideCodeNumber" class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">{{$t('코드번호')}}</label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_10 }}
                    </p>
                    <ml-input v-else :readonly="true" class="w-full" v-model="row.input_10" />
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('가능성')}}
                      <vx-tooltip :title="$t('가능성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_11 }}
                    </p>
                    <vs-select v-else class="w-full" autocomplete v-model="row.input_11" @change="handleChangeinput(row.id, row.input_11, row.input_12, 'input_13')">
                      <vs-select-item value="" text="-" />
                      <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber1" />
                    </vs-select>
                  </div>
                  <div class="mb-2">
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('중대성')}}
                      <vx-tooltip :title="$t('중대성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip2()" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_12 }}
                    </p>
                    <vs-select v-else class="w-full" autocomplete v-model="row.input_12" @change="handleChangeinput(row.id, row.input_11, row.input_12, 'input_13')">
                      <vs-select-item value="" text="-" />
                      <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber2" />
                    </vs-select>
                  </div>
                  <div>
                    <label class="text-xs text-gray-600 mb-1 block">
                      {{$t('위험도')}}
                      <vx-tooltip :title="$t('위험도이란?')"  :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip3()" />
                      </vx-tooltip>
                      <!-- AI 개선효과 툴팁 -->
                      <vx-tooltip
                        v-if="row.improvementEffect && row.improvementEffect.개선후_위험성 && !isPrint"
                        title="AI 분석 결과"
                        :html="'AI가 분석한 개선효과를<br/>확인하려면 클릭하세요'"
                        style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center"
                        position="top"
                        delay=".1s"
                      >
                        <feather-icon icon="ZapIcon" style="cursor: pointer; width: 16px; color: #667eea" @click="showImprovementEffectDialog(rowIndex)" />
                      </vx-tooltip>
                    </label>
                    <p v-if="isPrint" class="w-full flex-1">
                      {{ row.input_13 ? `${$t(getResultRisk(row.input_13))} (${row.input_11 * row.input_12})` : "" }}
                    </p>
                    <ml-input v-else :readonly="true" class="w-full" v-text="`${row.input_13 ? `${$t(getResultRisk(row.input_13))} (${row.input_11 * row.input_12})` : ''}`" :style="{ color: row.input_13_color }" />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Layout -->
    <div class="mobile-layout">
      <!-- Mobile Header -->
      <div v-if="list.length > 0" class="mobile-header">
        <div class="mobile-select-all">
          <vs-checkbox @change="handleCheckAll" :value="allChecked"></vs-checkbox>
          <span class="ml-2">{{$t('전체 선택')}}</span>
        </div>
        <div class="mobile-header-actions">
          <vs-button v-if="!isPrint" color="secondary" type="border" :disabled="selectedItemsCount === 0" @click="handleRemoveRow">선택삭제 ({{ selectedItemsCount }}) </vs-button>
        </div>
      </div>

      <!-- Mobile Cards -->
      <div class="mobile-cards-container">
        <!-- Empty State -->
        <div v-if="list.length === 0" class="mobile-empty-state">
          <feather-icon icon="InfoIcon" class="mb-3" style="color: #ccc" />
          <p class="text-gray-600">{{$t('s항목이 존재하지 않습니다.', { s: title })}}</p>
        </div>

        <!-- Risk Cards -->
        <div v-for="(row, rowIndex) in list" :key="row.id" class="mobile-risk-card">
          <!-- Card Header -->
          <div class="mobile-card-header">
            <div class="mobile-card-select">
              <vs-checkbox v-model="row.input_check" :readonly="readonly" />
              <span v-if="isNumbering" class="mobile-card-number">No. {{ rowIndex + 1 }}</span>
            </div>
            <vs-button v-if="!isPrint && row.input_check" color="danger" type="border" @click="handleRemoveSingleRow(rowIndex)">{{$t('삭제')}}</vs-button>
          </div>

          <!-- Card Content -->
          <div class="mobile-card-content">
            <!-- 세부작업명 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('세부작업명')}}</label>
              <p v-if="isPrint" class="mobile-field-text">{{ row.input_1 }}</p>
              <ml-textarea v-else class="mobile-textarea" :readonly="readonly" rows="4" v-model="row.input_1" placeholder="세부작업명을 입력하세요" @input="emitListChange()"/>
            </div>

            <!-- 평가구분/유해위험요인/재해형태 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('평가구분')}} / {{$t('유해·위험요인')}} / {{$t('재해형태')}}</label>
              <div v-if="!isPrint" class="mb-3">
                <AIButton
                  label="AI 분석"
                  icon="ZapIcon"
                  size="small"
                  :disabled="!row.input_1"
                  :loading="aiAnalyzingState[row.id]"
                  loading-text="분석중..."
                  tooltip="세부작업명을 기반으로 평가구분, 유해·위험요인, 재해형태를 AI가 자동 분석합니다"
                  @click="handleAIAnalyze(rowIndex)"
                />
              </div>

              <div class="mobile-sub-field">
                <span class="mobile-sub-label">{{$t('평가구분')}}</span>
                <p v-if="isPrint" class="mobile-field-text">{{ row.input_2 }}</p>
                <check-type-a-selector v-else v-model="row.input_2" :readonly="readonly || aiAnalyzingState[row.id]" @change="handleTypeInput(rowIndex)" />
              </div>

              <div class="mobile-sub-field">
                <span class="mobile-sub-label">{{$t('유해·위험요인')}}</span>
                <p v-if="isPrint" class="mobile-field-text">{{ row.input_4 }}</p>
                <vs-select v-else autocomplete v-model="row.input_4" :disabled="readonly || aiAnalyzingState[row.id]" :placeholder="$t('유해·위험요인을 선택하세요')">
                  <vs-select-item :key="index" :value="item.value" :text="item.text" v-for="(item, index) in handleAppraisalType(row.input_2)" />
                </vs-select>
              </div>

              <div class="mobile-sub-field">
                <span class="mobile-sub-label">{{$t('재해형태')}}</span>
                <p v-if="isPrint" class="mobile-field-text">{{ row.input_3 }}</p>
                <disaster-class-selector v-else v-model="row.input_3" :disabled="aiAnalyzingState[row.id]" />
              </div>
            </div>

            <!-- 현재 안전보건조치 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('현재 안전보건조치')}}</label>
              <div v-if="!isPrint" class="mb-3">
                <AIButton
                  label="AI 추천"
                  icon="ShieldIcon"
                  size="small"
                  :disabled="!row.input_1"
                  :loading="safetyMeasuresAnalyzingState[row.id]"
                  loading-text="분석중..."
                  tooltip="현재 상황에 적합한 안전보건조치를 AI가 추천합니다"
                  @click="handleAISafetyMeasures(rowIndex)"
                />
              </div>
              <p v-if="isPrint" class="mobile-field-text">{{ row.input_5 }}</p>
              <ml-textarea v-else class="mobile-textarea" :readonly="readonly" rows="4" v-model="row.input_5" placeholder="현재 안전보건조치를 입력하세요" @input="emitListChange()"/>
            </div>

            <!-- 현재 위험도 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('현재 위험도')}}</label>
              <div v-if="!isPrint" class="mb-3">
                <AIButton
                  label="AI 분석"
                  icon="TrendingUpIcon"
                  size="small"
                  :disabled="!row.input_1"
                  :loading="currentRiskAnalyzingState[row.id]"
                  loading-text="분석중..."
                  tooltip="현재 상황의 위험성 가능성과 중대성을 AI가 분석합니다"
                  @click="handleAICurrentRiskAnalyze(rowIndex)"
                />
              </div>

              <div class="mobile-risk-grid">
                <div class="mobile-risk-item">
                  <span class="mobile-risk-label">
                    {{$t('가능성')}}
                    <vx-tooltip :title="$t('가능성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">{{ row.input_6 }}</p>
                  <vs-select v-else autocomplete :disabled="readonly || currentRiskAnalyzingState[row.id]" v-model="row.input_6" @change="handleChangeinput(row.id, row.input_6, row.input_7, 'input_8')">
                    <vs-select-item value="" text="-" />
                    <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber1" />
                  </vs-select>
                </div>

                <div class="mobile-risk-item">
                  <span class="mobile-risk-label">
                    {{$t('중대성')}}
                    <vx-tooltip :title="$t('중대성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip2()" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">{{ row.input_7 }}</p>
                  <vs-select v-else autocomplete :disabled="readonly || currentRiskAnalyzingState[row.id]" v-model="row.input_7" @change="handleChangeinput(row.id, row.input_6, row.input_7, 'input_8')">
                    <vs-select-item value="" text="-" />
                    <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber2" />
                  </vs-select>
                </div>

                <div class="mobile-risk-item mobile-risk-result">
                  <span class="mobile-risk-label">
                    {{$t('위험도')}}
                    <vx-tooltip :title="$t('위험도이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip3()" />
                    </vx-tooltip>
                    <vx-tooltip
                      v-if="row.riskAnalysis && row.riskAnalysis.평가근거 && !isPrint"
                      title="AI 분석 결과"
                      :html="'AI가 분석한 위험도 평가근거를<br/>확인하려면 클릭하세요'"
                      style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center"
                      position="top"
                      delay=".1s"
                    >
                      <feather-icon icon="ZapIcon" style="cursor: pointer; width: 16px; color: #667eea" @click="showRiskAnalysisDialog(rowIndex)" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">
                    {{ row.input_8 ? `${$t(getResultRisk(row.input_8))} (${row.input_6 * row.input_7})` : "" }}
                  </p>
                  <div v-else class="mobile-risk-value" :style="{ color: row.input_8_color }">
                    {{ row.input_8 ? `${$t(getResultRisk(row.input_8))} (${row.input_6 * row.input_7})` : "-" }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 위험감소대책 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('위험감소대책')}}</label>
              <div v-if="!isPrint" class="mb-3">
                <AIButton
                  label="AI 추천"
                  icon="ShieldIcon"
                  size="small"
                  :disabled="!row.input_1"
                  :loading="riskReductionsAnalyzingState[row.id]"
                  loading-text="분석중..."
                  tooltip="위험을 감소시킬 수 있는 대책을 AI가 추천합니다"
                  @click="handleAIRiskReductions(rowIndex)"
                />
              </div>
              <p v-if="isPrint" class="mobile-field-text">{{ row.input_9 }}</p>
              <ml-textarea v-else class="mobile-textarea" :readonly="readonly || riskReductionsAnalyzingState[row.id]" rows="4" v-model="row.input_9" placeholder="위험감소대책을 입력하세요" @input="emitListChange()"/>
            </div>

            <!-- 개선후 위험도 -->
            <div class="mobile-field">
              <label class="mobile-field-label">{{$t('개선후 위험도')}}</label>
              <div v-if="!isPrint" class="mb-3">
                <AIButton
                  label="AI 분석"
                  icon="TrendingUpIcon"
                  size="small"
                  :disabled="!row.input_1 || !row.input_9"
                  :loading="afterRiskAnalyzingState[row.id]"
                  loading-text="분석중..."
                  tooltip="위험감소대책 적용 후의 개선된 위험성을 AI가 분석합니다"
                  @click="handleAIAfterRiskAnalyze(rowIndex)"
                />
              </div>

              <div v-if="!hideCodeNumber" class="mobile-sub-field">
                <span class="mobile-sub-label">{{$t('코드번호')}}</span>
                <p v-if="isPrint" class="mobile-field-text">{{ row.input_10 }}</p>
                <ml-input v-else :readonly="true" v-model="row.input_10" />
              </div>

              <div class="mobile-risk-grid">
                <div class="mobile-risk-item">
                  <span class="mobile-risk-label">
                    {{$t('가능성')}}
                    <vx-tooltip :title="$t('가능성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip1()" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">{{ row.input_11 }}</p>
                  <vs-select v-else autocomplete v-model="row.input_11" @change="handleChangeinput(row.id, row.input_11, row.input_12, 'input_13')">
                    <vs-select-item value="" text="-" />
                    <vs-select-item :key="index" :value="item.value" :text="$t(item.label)" v-for="(item, index) in optionsNumber1" />
                  </vs-select>
                </div>

                <div class="mobile-risk-item">
                  <span class="mobile-risk-label">
                    {{$t('중대성')}}
                    <vx-tooltip :title="$t('중대성이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip2()" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">{{ row.input_12 }}</p>
                  <vs-select v-else autocomplete v-model="row.input_12" @change="handleChangeinput(row.id, row.input_11, row.input_12, 'input_13')">
                    <vs-select-item value="" text="-" />
                    <vs-select-item :key="index" :value="item.value" :text="$t($t(item.label))" v-for="(item, index) in optionsNumber2" />
                  </vs-select>
                </div>

                <div class="mobile-risk-item mobile-risk-result">
                  <span class="mobile-risk-label">
                    {{$t('위험도')}}
                    <vx-tooltip :title="$t('위험도이란?')" :html="$t('자세한 정보를 보시려면<br>느낌표ⓘ 아이콘을 클릭하세요')" style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center" position="top" delay=".1s">
                      <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" @click="onClickToolTip3()" />
                    </vx-tooltip>
                    <vx-tooltip
                      v-if="row.improvementEffect && row.improvementEffect.개선후_위험성 && !isPrint"
                      title="AI 분석 결과"
                      :html="'AI가 분석한 개선효과를<br/>확인하려면 클릭하세요'"
                      style="margin-left: 4px; display: inline-flex; align-items: center; justify-content: center"
                      position="top"
                      delay=".1s"
                    >
                      <feather-icon icon="ZapIcon" style="cursor: pointer; width: 16px; color: #667eea" @click="showImprovementEffectDialog(rowIndex)" />
                    </vx-tooltip>
                  </span>
                  <p v-if="isPrint" class="mobile-field-text">
                    {{ row.input_13 ? `${$t(getResultRisk(row.input_13))} (${row.input_11 * row.input_12})` : "" }}
                  </p>
                  <div v-else class="mobile-risk-value" :style="{ color: row.input_13_color }">
                    {{ row.input_13 ? `${$t(getResultRisk(row.input_13))} (${row.input_11 * row.input_12})` : "-" }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Add Button -->
      <div v-if="!isPrint" class="mobile-add-button">
        <vs-button color="secondary" @click="handleAddRow" class="mobile-add-btn">
          <feather-icon icon="PlusIcon" class="mr-2" />
          {{$t('항목추가')}}
        </vs-button>
      </div>
    </div>
    <!--    <div class="text-box">-->
    <!--      <div class="center-flex h-12 bg-1">-->
    <!--        <feather-icon-->
    <!--          icon="AlertTriangleIcon"-->
    <!--          class="ml-1"-->
    <!--          style="width:18px; height:18px;"-->
    <!--        />-->
    <!--        위험성 결정-->
    <!--      </div>-->
    <!--      <div class="p-4 text-sm font-medium ">-->
    <!--        <p>-->
    <!--          ※ 가능성 : 5(매우 높음), 4(높음), 3(보통), 2(낮음), 1(매우 낮음)-->
    <!--        </p>-->
    <!--        <p>-->
    <!--          ※ 중대성 : 4(사망, 장애발생), 3(휴업필요 부상/질병), 2(휴업 불필요-->
    <!--          부상/질병), 1(비치료)-->
    <!--        </p>-->
    <!--        <p>-->
    <!--          ※ 위험성 : 가능성 X 중대성-->
    <!--        </p>-->
    <!--        <p>-->
    <!--          ※ 위험성 감소대책 : 위험성이 8 이상인 유해·위험요인에 대해서는 현재-->
    <!--          조치되어 있지 않은 위험성 감소대책을 수립·기재-->
    <!--        </p>-->
    <!--        <p>-->
    <!--          ※ 코드NO : (등급)-(연번) ex)A-1, B-2-->
    <!--        </p>-->
    <!--      </div>-->
    <!--    </div>-->

    <popup-risk-info_3x3_1 v-model="showInfoPopup1"></popup-risk-info_3x3_1>
    <popup-risk-info_3x3_2 v-model="showInfoPopup2"></popup-risk-info_3x3_2>
    <popup-risk-info_3x3_3 v-model="showInfoPopup3"></popup-risk-info_3x3_3>
    <popup-a-i-safety-measures v-model="showSafetyMeasuresPopup" :requestData="safetyMeasuresRequestData" @selectComplete="handleSafetyMeasuresSelectComplete" />
    <popup-a-i-risk-reductions v-model="showRiskReductionsPopup" :requestData="riskReductionsRequestData" @selectComplete="handleRiskReductionsSelectComplete" />
    <popup-improvement-effect v-model="showImprovementEffectPopup" :improvementData="currentImprovementData" :currentRisk="currentRiskValue" />
    <popup-risk-analysis v-model="showRiskAnalysisPopup" :riskAnalysisData="currentRiskAnalysisData" />
  </div>
</template>

<script>
import DisasterClassSelector from "@/components/selector/DisasterClassSelector.vue";
import CheckTypeASelector from "@/components/selector/CheckTypeASelector.vue";
import PopupRiskInfo_3x3_1 from "@/components/TableRiskAppraisal_3x3/PopupRiskInfo_3x3_1.vue";
import PopupRiskInfo_3x3_2 from "@/components/TableRiskAppraisal_3x3/PopupRiskInfo_3x3_2.vue";
import PopupRiskInfo_3x3_3 from "@/components/TableRiskAppraisal_3x3/PopupRiskInfo_3x3_3.vue";
import PopupAISafetyMeasures from "@/popup/PopupAISafetyMeasures.vue";
import PopupAIRiskReductions from "@/popup/PopupAIRiskReductions.vue";
import PopupImprovementEffect from "@/popup/PopupImprovementEffect.vue";
import PopupRiskAnalysis from "@/popup/PopupRiskAnalysis.vue";
import AIButton from "@/components/AIButton.vue";
export default {
  components: {
    PopupRiskInfo_3x3_3,
    PopupRiskInfo_3x3_2,
    PopupRiskInfo_3x3_1,
    CheckTypeASelector,
    DisasterClassSelector,
    PopupAISafetyMeasures,
    PopupAIRiskReductions,
    PopupImprovementEffect,
    PopupRiskAnalysis,
    AIButton,
  },
  emits: ["input", "ai-recommendation", "image-ai-recommendation"],
  props: {
    title: {
      type: String,
      default: () => "4M 위험성 평가표",
    },
    isPrint: Boolean,
    readonly: Boolean,
    hideCodeNumber: Boolean,
    value: Array,
    isNumbering: {
      type: Boolean,
      default: false,
    },
    aiRecommendationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.initializeList(value);
      },
    },
  },
  computed: {
    allChecked() {
      let allCheck = true;
      for (const item of this.list) {
        if (!item.input_check) {
          allCheck = false;
          break;
        }
      }
      return allCheck;
    },
    selectedItemsCount() {
      return this.list.filter((item) => item.input_check).length;
    },
  },
  data() {
    return {
      optionsNumber1: [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
      ],
      optionsNumber2: [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
      ],
      showInfoPopup1: false,
      showInfoPopup2: false,
      showInfoPopup3: false,
      showSafetyMeasuresPopup: false,
      safetyMeasuresRequestData: {},
      currentSafetyMeasuresRowIndex: null,
      // UI 상태 관리용 로컬 변수들 (DB에 저장되지 않음)
      aiAnalyzingState: {}, // { rowId: true/false }
      aiApplyingState: {}, // AI 응답값 적용 중(평가구분 변경에 따른 input_4 리셋 캐스케이드 차단용)
      safetyMeasuresAnalyzingState: {}, // { rowId: true/false }
      currentRiskAnalyzingState: {}, // { rowId: true/false }
      riskReductionsAnalyzingState: {}, // { rowId: true/false }
      showRiskReductionsPopup: false,
      riskReductionsRequestData: {},
      currentRiskReductionsRowIndex: null,
      afterRiskAnalyzingState: {}, // { rowId: true/false }
      showImprovementEffectPopup: false,
      currentImprovementData: null,
      currentRiskValue: 0,
      aiUpdatingImprovement: {}, // AI가 개선후 위험도를 업데이트 중인 상태 (제거 방지용)
      showRiskAnalysisPopup: false,
      currentRiskAnalysisData: null,
      // AI 분석 캐시 — 마지막 호출 시의 입력값 키를 저장
      aiAnalyzeLastInput: {},        // handleAIAnalyze 입력 캐시
      currentRiskLastInput: {},      // handleAICurrentRiskAnalyze 입력 캐시
      afterRiskLastInput: {},        // handleAIAfterRiskAnalyze 입력 캐시
      list: [],
    };
  },
  methods: {
    initializeList(value) {
      if (!value) {
        this.list = [];
        return;
      }

      // 순수한 데이터만 저장 (UI 상태 변수는 제외)
      this.list = value.map((item) => {
        // eslint-disable-next-line no-unused-vars
        const { aiAnalyzing, safetyMeasuresAnalyzing, ...pureItem } = item;
        return pureItem;
      });

      // UI 상태 초기화
      this.aiAnalyzingState = {};
      this.aiApplyingState = {};
      this.safetyMeasuresAnalyzingState = {};
      this.currentRiskAnalyzingState = {};
      this.riskReductionsAnalyzingState = {};
      this.afterRiskAnalyzingState = {};
      this.aiUpdatingImprovement = {};
      this.aiAnalyzeLastInput = {};
      this.currentRiskLastInput = {};
      this.afterRiskLastInput = {};
    },

    emitListChange() {
      // UI 상태 변수들을 제거한 순수한 데이터만 전달
      const pureList = this.list.map((item) => {
        // eslint-disable-next-line no-unused-vars
        const { aiAnalyzing, safetyMeasuresAnalyzing, ...pureItem } = item;
        return pureItem;
      });
      this.$emit("input", pureList);
    },

    getResultRisk(data) {
      switch (data) {
      case "A":
        return "높음";
      case "B":
        return "보통";
      case "C":
        return "낮음";
      }
    },

    handleCheckAll(e) {
      const checked = e.target.checked;
      this.list.forEach((e) => {
        e.input_check = checked;
      });
      this.emitListChange();
    },

    handleImageAIRecommendation() {
      this.$emit("image-ai-recommendation");
    },

    handleAIRecommendation() {
      // AI 추천 이벤트를 부모 컴포넌트로 전송
      this.$emit("ai-recommendation");
    },
    handleAddRow() {
      // 고유한 ID 생성 (현재 시간 + 랜덤값 사용)
      const newId = Date.now() + Math.random();
      this.list.push({
        id: newId,
        input_check: false,
        input_1: "",
        input_2: "",
        input_3: "",
        input_4: "",
        input_5: "",
        input_6: "",
        input_7: "",
        input_8: "",
        input_8_color: "#28C76F",
        input_9: "",
        input_10: "C-1",
        input_11: "",
        input_12: "",
        input_13: "",
        input_13_color: "#28C76F",
      });
      this.emitListChange();
    },
    handleRemoveRow() {
      // 체크된 아이템들을 삭제 (체크되지 않은 아이템들만 남김)
      this.list = this.list.filter((x) => x.input_check !== true);
      this.emitListChange();
    },
    handleRemoveSingleRow(index) {
      // 단일 행 삭제 (모바일용)
      this.list.splice(index, 1);
      this.emitListChange();
    },
    onClickToolTip1() {
      this.showInfoPopup1 = true;
    },
    onClickToolTip2() {
      this.showInfoPopup2 = true;
    },
    onClickToolTip3() {
      this.showInfoPopup3 = true;
    },
    handleChangeinput(id, n1, n2, string) {
      let c = 1;
      let b = 1;
      let a = 1;
      const value = n1 * n2;
      const color = `${string}_color`;
      const result = this.handleResult(value);
      this.list.forEach((i, index) => {
        for (const key in i) {
          if (key === string && i.id === id) {
            this.list[index][key] = result;
            this.list[index][color] = this.checkColor(value);
          }
          if (key.includes("_8")) {
            if (this.list[index][key] === "C") {
              this.list[index].input_10 = `C-${c}`;
              c++;
            } else if (this.list[index][key] === "B") {
              this.list[index].input_10 = `B-${b}`;
              b++;
            } else if (this.list[index][key] === "A") {
              this.list[index].input_10 = `A-${a}`;
              a++;
            }
          }
        }
      });
      this.emitListChange();
    },
    checkColor(value) {
      if (value >= 1 && value <= 2) {
        return "#28C76F";
      } else if (value >= 3 && value <= 4) {
        return "#FF8510";
      } else {
        return "#E42728";
      }
    },
    handleResult(result) {
      if (result >= 1 && result <= 2) return "C";
      else if (result >= 3 && result <= 4) return "B";
      else if (result >= 6 && result <= 9) return "A";
    },
    handleTypeInput(index) {
      const row = this.list[index];
      // AI 분석 결과 적용 중에는 평가구분 변경 캐스케이드로 인한 input_4 리셋을 차단
      if (row && this.aiApplyingState[row.id]) return;
      this.list[index].input_4 = "";
      this.emitListChange();
    },
    handleAppraisalType(type) {
      if (type === "기계(설비)적 요인") {
        return [
          { text: "끼임(감김)", value: "끼임(감김)" },
          { text: "위험한 표면(절단, 베임, 긁힘)", value: "위험한 표면(절단, 베임, 긁힘)" },
          { text: "기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분", value: "기계(설비)의 낙하, 비래, 전복, 붕괴, 전도 위험 부분" },
          { text: "충돌위험 부분", value: "충돌위험 부분" },
          { text: "넘어짐(미끄러짐, 걸림, 헛디딤)", value: "넘어짐(미끄러짐, 걸림, 헛디딤)" },
          { text: "추락위험 부분(개구부 등)", value: "추락위험 부분(개구부 등)" },
        ];
      } else if (type === "전기적 요인") {
        return [
          { text: "감전(안전전압초과)", value: "감전(안전전압초과)" },
          { text: "아크", value: "아크" },
          { text: "정전기", value: "정전기" },
          { text: "화재 / 폭발 위험", value: "화재 / 폭발 위험" },
        ];
      } else if (type === "화학(물질)적 요인") {
        return [
          { text: "가스", value: "가스" },
          { text: "증기", value: "증기" },
          { text: "에어로졸·흄", value: "에어로졸·흄" },
          { text: "액체·미스트", value: "액체·미스트" },
          { text: "고체(분진)", value: "고체(분진)" },
          { text: "반응성 물질", value: "반응성 물질" },
          { text: "방사선", value: "방사선" },
          { text: "화재 / 폭발 위험", value: "화재 / 폭발 위험" },
          { text: "복사열 / 폭발과압", value: "복사열 / 폭발과압" },
        ];
      } else if (type === "생물학적 요인") {
        return [
          { text: "병원성 미생물, 바이러스에 의한 감염", value: "병원성 미생물, 바이러스에 의한 감염" },
          { text: "유전자 변형물질(GMO)", value: "유전자 변형물질(GMO)" },
          { text: "알러지 및 미생물", value: "알러지 및 미생물" },
          { text: "동물", value: "동물" },
          { text: "식물", value: "식물" },
        ];
      } else if (type === "작업특성 요인") {
        return [
          { text: "소음", value: "소음" },
          { text: "초음파·초저주파음", value: "초음파·초저주파음" },
          { text: "진동", value: "진동" },
          { text: "근로자 실수(휴먼에러)", value: "근로자 실수(휴먼에러)" },
          { text: "저압 또는 고압상태", value: "저압 또는 고압상태" },
          { text: "질식위험·산소결핍", value: "질식위험·산소결핍" },
          { text: "중량물 취급작업", value: "중량물 취급작업" },
          { text: "반복작업", value: "반복작업" },
          { text: "불안정한 작업자세", value: "불안정한 작업자세" },
          { text: "작업(조작)도구", value: "작업(조작)도구" },
          { text: "기후 / 고온 / 한랭", value: "기후 / 고온 / 한랭" },
        ];
      } else if (type === "작업환경 요인") {
        return [
          { text: "기후 / 고온 / 한랭", value: "기후 / 고온 / 한랭" },
          { text: "조명", value: "조명" },
          { text: "공간 및 이동통로", value: "공간 및 이동통로" },
          { text: "주변 근로자", value: "주변 근로자" },
          { text: "작업시간", value: "작업시간" },
          { text: "조직 안전문화", value: "조직 안전문화" },
          { text: "화상", value: "화상" },
          { text: "작업(조작) 도구", value: "작업(조작) 도구" },
        ];
      }
    },

    async handleAIAnalyze(rowIndex) {
      const row = this.list[rowIndex];

      // 이미 분석 중인 경우 중복 실행 방지
      if (this.aiAnalyzingState[row.id]) {
        return;
      }

      if (row.input_1 === "" || row.input_1 === null || row.input_1 === undefined) {
        this.$vs.notify({
          title: "AI 분석 오류",
          text: "세부작업명을 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // 입력값이 이전과 동일하고 결과가 이미 있으면 API 호출 생략
      const analyzeInputKey = row.input_1;
      if (
        this.aiAnalyzeLastInput[row.id] === analyzeInputKey &&
        row.input_2
      ) {
        this.$vs.notify({
          title: "AI 분석 결과",
          text: "입력 데이터가 변경되지 않아 기존 분석 결과를 유지합니다.",
          color: "success",
          position: "top-right",
        });
        return;
      }

      try {
        // 로딩 상태 시작
        this.$set(this.aiAnalyzingState, row.id, true);

        // 세부작업명을 설명으로 사용
        const params = {
          세부작업명: row.input_1,
        };

        // Vuex action 호출
        const response = await this.$store.dispatch("miras/DEFINE_RISK_TYPE", params);

        if (response.success && response.data) {
          // AI 응답값 적용 시작 — 평가구분 변경에 따른 input_4 리셋 캐스케이드를 차단
          this.$set(this.aiApplyingState, row.id, true);

          // 1) 평가구분 먼저 세팅
          //    (CheckTypeASelector → vs-select가 프로그래매틱 v-model 변경에도 @change를 emit하므로
          //     handleTypeInput이 호출되지만 aiApplyingState로 input_4 리셋이 차단됨)
          this.list[rowIndex].input_2 = response.data.평가구분 || "";

          // 2) 평가구분 변경에 의한 캐스케이드(@change → handleTypeInput)가 처리된 다음 tick에서
          //    input_4 / input_3 적용
          await this.$nextTick();

          // 유해·위험요인 정규화: 현재 평가구분의 옵션 목록에 정확히 일치하는 값일 때만 채움
          const allowedHazards = (this.handleAppraisalType(this.list[rowIndex].input_2) || []).map((o) => o.value);
          const aiHazard = response.data.유해위험요인 || "";
          this.list[rowIndex].input_4 = allowedHazards.includes(aiHazard) ? aiHazard : "";

          // 재해형태
          this.list[rowIndex].input_3 = response.data.재해형태 || "";

          // 캐스케이드 차단 해제는 한 tick 더 뒤(컴포넌트 내부 watcher 전파 완료 후)
          await this.$nextTick();
          this.$set(this.aiApplyingState, row.id, false);

          // 캐시 키 저장
          this.$set(this.aiAnalyzeLastInput, row.id, analyzeInputKey);

          // 데이터 변경 emit
          this.emitListChange();

          // 성공 알림
          this.$vs.notify({
            title: "AI 분석 완료",
            text: "위험 요인이 성공적으로 분석되었습니다.",
            color: "success",
            position: "top-right",
          });
        } else {
          throw new Error("AI 분석 응답이 올바르지 않습니다.");
        }
      } catch (error) {
        console.error("AI 분석 오류:", error);

        // 오류 알림
        this.$vs.notify({
          title: "AI 분석 오류",
          text: "분석 중 오류가 발생했습니다. 다시 시도해주세요.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        // 로딩 상태 종료
        this.$set(this.aiAnalyzingState, row.id, false);
        this.$set(this.aiApplyingState, row.id, false);
      }
    },

    handleAISafetyMeasures(rowIndex) {
      const row = this.list[rowIndex];

      if (row.input_1 === "" || row.input_1 === null || row.input_1 === undefined) {
        this.$vs.notify({
          title: "AI 안전보건조치 추천 오류",
          text: "세부작업명을 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // 요청 데이터 설정
      this.safetyMeasuresRequestData = {
        평가구분: row.input_2 || "",
        유해위험요인: row.input_4 || "",
        설명: row.input_1 || "",
        재해형태: row.input_3 || "",
      };

      this.currentSafetyMeasuresRowIndex = rowIndex;
      this.showSafetyMeasuresPopup = true;
    },

    handleSafetyMeasuresSelectComplete(selectedData) {
      if (this.currentSafetyMeasuresRowIndex !== null && selectedData.length > 0) {
        // 선택된 항목들을 "-" 기호와 줄바꿈으로 조합
        const formattedText = selectedData.map((item) => `- ${item.text}`).join("\n");

        // 기존 내용에 추가하거나 새로 설정
        const currentText = this.list[this.currentSafetyMeasuresRowIndex].input_5;
        if (currentText && currentText.trim() !== "") {
          this.list[this.currentSafetyMeasuresRowIndex].input_5 = `${currentText}\n${formattedText}`;
        } else {
          this.list[this.currentSafetyMeasuresRowIndex].input_5 = formattedText;
        }

        this.emitListChange();

        // 성공 알림
        this.$vs.notify({
          title: "AI 안전보건조치 추천 완료",
          text: `${selectedData.length}개의 안전보건조치가 추가되었습니다.`,
          color: "success",
          position: "top-right",
        });

        // 상태 초기화
        this.currentSafetyMeasuresRowIndex = null;
      }
    },

    async handleAICurrentRiskAnalyze(rowIndex) {
      const row = this.list[rowIndex];

      // 이미 분석 중인 경우 중복 실행 방지
      if (this.currentRiskAnalyzingState[row.id]) {
        return;
      }

      if (row.input_1 === "" || row.input_1 === null || row.input_1 === undefined) {
        this.$vs.notify({
          title: "AI 위험도 분석 오류",
          text: "세부작업명을 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // 입력값이 이전과 동일하고 결과가 이미 있으면 팝업만 재표시
      const currentRiskInputKey = [row.input_2, row.input_4, row.input_1, row.input_3, row.input_5].join("|");
      if (
        this.currentRiskLastInput[row.id] === currentRiskInputKey &&
        row.riskAnalysis &&
        row.riskAnalysis.가능성
      ) {
        this.currentRiskAnalysisData = row.riskAnalysis;
        if (row.riskAnalysis.평가근거) {
          this.showRiskAnalysisPopup = true;
        } else {
          this.$vs.notify({
            title: "AI 위험도 분석 결과",
            text: "입력 데이터가 변경되지 않아 기존 분석 결과를 유지합니다.",
            color: "success",
            position: "top-right",
          });
        }
        return;
      }

      try {
        // 로딩 상태 시작
        this.$set(this.currentRiskAnalyzingState, row.id, true);

        // 파라미터 설정
        const params = {
          평가구분: row.input_2 || "",
          유해위험요인: row.input_4 || "",
          설명: row.input_1, // 필수
          재해형태: row.input_3 || "",
          현재조치: row.input_5 || "",
        };

        // Vuex action 호출
        const response = await this.$store.dispatch("miras/MEASURE_CURRENT_RISK_POINT", params);

        if (response.success && response.data) {
          // 응답 데이터로 현재 위험도 필드 값 설정
          if (response.data.가능성) {
            this.list[rowIndex].input_6 = response.data.가능성;
          }
          if (response.data.중대성) {
            this.list[rowIndex].input_7 = response.data.중대성;
          }

          // 위험도 분석 결과 데이터를 테이블 데이터에 포함 (전체 response.data 저장)
          this.list[rowIndex].riskAnalysis = {
            가능성: response.data.가능성 || "",
            중대성: response.data.중대성 || "",
            위험성: response.data.위험성 || "",
            관리기준: response.data.관리기준 || "",
            평가근거: response.data.평가근거 || null,
          };

          // 가능성과 중대성이 업데이트되면 위험도도 자동 계산
          if (response.data.가능성 && response.data.중대성) {
            this.handleChangeinput(row.id, response.data.가능성, response.data.중대성, "input_8");
          }

          // 캐시 키 저장
          this.$set(this.currentRiskLastInput, row.id, currentRiskInputKey);

          // 데이터 변경 emit
          this.emitListChange();

          // 평가근거가 있는 경우 자동으로 팝업 표시
          if (response.data.평가근거) {
            this.currentRiskAnalysisData = this.list[rowIndex].riskAnalysis;
            this.showRiskAnalysisPopup = true;
          }

          // 성공 알림
          this.$vs.notify({
            title: "AI 위험도 분석 완료",
            text: "현재 위험도가 성공적으로 분석되었습니다.",
            color: "success",
            position: "top-right",
          });
        } else {
          throw new Error("AI 위험도 분석 응답이 올바르지 않습니다.");
        }
      } catch (error) {
        console.error("AI 위험도 분석 오류:", error);

        // 오류 알림
        this.$vs.notify({
          title: "AI 위험도 분석 오류",
          text: error.message || "분석 중 오류가 발생했습니다. 다시 시도해주세요.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        // 로딩 상태 종료
        this.$set(this.currentRiskAnalyzingState, row.id, false);
      }
    },

    handleAIRiskReductions(rowIndex) {
      const row = this.list[rowIndex];

      if (row.input_1 === "" || row.input_1 === null || row.input_1 === undefined) {
        this.$vs.notify({
          title: "AI 위험감소대책 추천 오류",
          text: "세부작업명을 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // 요청 데이터 설정
      this.riskReductionsRequestData = {
        평가구분: row.input_2 || "",
        유해위험요인: row.input_4 || "",
        설명: row.input_1, // 필수
        재해형태: row.input_3 || "",
        현재조치: row.input_5 || "",
        가능성: row.input_6 || "",
        중대성: row.input_7 || "",
      };

      this.currentRiskReductionsRowIndex = rowIndex;
      this.showRiskReductionsPopup = true;
    },

    handleRiskReductionsSelectComplete(selectedData) {
      if (this.currentRiskReductionsRowIndex !== null && selectedData.length > 0) {
        // 선택된 항목들을 "-" 기호와 줄바꿈으로 조합
        const formattedText = selectedData.map((item) => `- ${item.text}`).join("\n");

        // 기존 내용에 추가하거나 새로 설정
        const currentText = this.list[this.currentRiskReductionsRowIndex].input_9;
        if (currentText && currentText.trim() !== "") {
          this.list[this.currentRiskReductionsRowIndex].input_9 = `${currentText}\n${formattedText}`;
        } else {
          this.list[this.currentRiskReductionsRowIndex].input_9 = formattedText;
        }

        this.emitListChange();

        // 성공 알림
        this.$vs.notify({
          title: "AI 위험감소대책 추천 완료",
          text: `${selectedData.length}개의 위험감소대책이 추가되었습니다.`,
          color: "success",
          position: "top-right",
        });

        // 상태 초기화
        this.currentRiskReductionsRowIndex = null;
      }
    },

    async handleAIAfterRiskAnalyze(rowIndex) {
      const row = this.list[rowIndex];

      // 이미 분석 중인 경우 중복 실행 방지
      if (this.afterRiskAnalyzingState[row.id]) {
        return;
      }

      // 필수 파라미터 검증
      if (row.input_1 === "" || row.input_1 === null || row.input_1 === undefined) {
        this.$vs.notify({
          title: "AI 개선후 위험성 분석 오류",
          text: "세부작업명을 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      if (!row.input_6 || !row.input_7 || !row.input_9) {
        this.$vs.notify({
          title: "AI 개선후 위험성 분석 오류",
          text: "현재 위험도(가능성, 중대성)와 위험감소대책을 모두 입력해주세요.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      // 입력값이 이전과 동일하고 결과가 이미 있으면 팝업만 재표시
      const afterRiskInputKey = [
        row.input_2, row.input_4, row.input_1, row.input_3,
        row.input_5, row.input_6, row.input_7, row.input_9,
      ].join("|");
      if (
        this.afterRiskLastInput[row.id] === afterRiskInputKey &&
        row.improvementEffect &&
        row.improvementEffect.개선후_위험성
      ) {
        const input6 = Number(row.input_6) || 0;
        const input7 = Number(row.input_7) || 0;
        this.currentImprovementData = row.improvementEffect;
        this.currentRiskValue = input6 * input7;
        this.showImprovementEffectPopup = true;
        return;
      }

      try {
        // 로딩 상태 시작
        this.$set(this.afterRiskAnalyzingState, row.id, true);

        // 파라미터 설정
        const params = {
          평가구분: row.input_2 || "",
          유해위험요인: row.input_4 || "",
          설명: row.input_1, // 필수
          재해형태: row.input_3 || "",
          현재조치: row.input_5 || "",
          가능성: row.input_6, // 필수
          중대성: row.input_7, // 필수
          위험감소대책: row.input_9, // 필수
        };

        // Vuex action 호출
        const response = await this.$store.dispatch("miras/MEASURE_AFTER_RISK_POINT", params);

        if (response.success && response.data) {
          // 현재 위험성 계산 (팝업 표시용)
          const input6 = Number(row.input_6) || 0;
          const input7 = Number(row.input_7) || 0;
          const currentRiskValue = input6 * input7;

          // 응답 데이터로 개선후 위험도 필드 값 설정
          if (response.data.개선후_가능성) {
            this.list[rowIndex].input_11 = response.data.개선후_가능성;
          }
          if (response.data.개선후_중대성) {
            this.list[rowIndex].input_12 = response.data.개선후_중대성;
          }

          // 개선효과 데이터를 테이블 데이터에 포함 (전체 response.data 저장)
          const afterScore = (response.data.개선후_가능성 && response.data.개선후_중대성)
            ? response.data.개선후_가능성 * response.data.개선후_중대성
            : null;
          this.list[rowIndex].improvementEffect = {
            개선후_위험성: response.data.개선후_위험성 || "",
            개선후_점수: afterScore,
            개선후_관리기준: response.data.개선후_관리기준 || "",
            가능성_변화: (response.data.개선효과 && response.data.개선효과.가능성_변화) || "",
            중대성_변화: (response.data.개선효과 && response.data.개선효과.중대성_변화) || "",
            위험성_변화: (response.data.개선효과 && response.data.개선효과.위험성_변화) || "",
            주요_개선요인: (response.data.개선효과 && response.data.개선효과.주요_개선요인) || "",
          };

          // AI가 개선후 위험도를 업데이트 중임을 표시 (개선효과 제거 방지용)
          this.$set(this.aiUpdatingImprovement, row.id, true);

          // Vue의 반응성을 위해 nextTick 사용하여 DOM 업데이트 후 계산 수행
          this.$nextTick(() => {
            // 개선후 가능성과 중대성이 업데이트되면 위험도도 자동 계산
            if (response.data.개선후_가능성 && response.data.개선후_중대성) {
              this.handleChangeinput(row.id, response.data.개선후_가능성, response.data.개선후_중대성, "input_13");
            }

            // AI 업데이트 완료 - 제거 방지 해제
            this.$set(this.aiUpdatingImprovement, row.id, false);

            // 캐시 키 저장
            this.$set(this.afterRiskLastInput, row.id, afterRiskInputKey);

            // 데이터 변경 emit
            this.emitListChange();

            // 개선효과 팝업 표시
            this.currentImprovementData = this.list[rowIndex].improvementEffect;
            this.currentRiskValue = currentRiskValue;
            this.showImprovementEffectPopup = true;

            // 성공 알림
            this.$vs.notify({
              title: "AI 개선후 위험성 분석 완료",
              text: "개선후 위험성이 성공적으로 분석되었습니다.",
              color: "success",
              position: "top-right",
            });
          });
        } else {
          throw new Error("AI 개선후 위험성 분석 응답이 올바르지 않습니다.");
        }
      } catch (error) {
        console.error("AI 개선후 위험성 분석 오류:", error);

        // 오류 알림
        this.$vs.notify({
          title: "AI 개선후 위험성 분석 오류",
          text: error.message || "분석 중 오류가 발생했습니다. 다시 시도해주세요.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        // 로딩 상태 종료
        this.$set(this.afterRiskAnalyzingState, row.id, false);
        // AI 업데이트 상태도 확실히 정리
        this.$set(this.aiUpdatingImprovement, row.id, false);
      }
    },

    showImprovementEffectDialog(rowIndex) {
      const row = this.list[rowIndex];
      if (row.improvementEffect && row.improvementEffect.개선후_위험성) {
        this.currentImprovementData = row.improvementEffect;
        // 현재 위험성 계산 (number로 변환하고 NaN 체크)
        const input6 = Number(row.input_6) || 0;
        const input7 = Number(row.input_7) || 0;
        this.currentRiskValue = input6 * input7;
        this.showImprovementEffectPopup = true;
      }
    },

    showRiskAnalysisDialog(rowIndex) {
      const row = this.list[rowIndex];
      if (row.riskAnalysis && row.riskAnalysis.평가근거) {
        this.currentRiskAnalysisData = row.riskAnalysis;
        this.showRiskAnalysisPopup = true;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
$w-inpu0: 260px;
$w-inpu1: 220px;
$w-inpu2: 140px;
.center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.bg-1 {
  background-color: #f9f9f9;
  border-bottom: $border;
}

.label-text {
  width: 114px;
  font-weight: 500;
  font-size: 14px;
}

.mt-component-form {
  margin-top: 15px;
}

.text-box {
  line-height: 19px;
  margin-top: 15px;
  border: $border;
}

/* Table Styles */
.table-container {
  border: $border;
  overflow: auto;
  width: 100%;
}

.risk-appraisal-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.table-header {
  background-color: #f9f9f9;
}

.table-header-cell {
  border: $border;
  padding: 15px 7px;
  text-align: center;
  vertical-align: middle;
  font-weight: 700;
  font-size: 14px;
  min-height: 80px;
}

.table-cell {
  border: $border;
  padding: 7px;
  vertical-align: top;
  height: 100%;
}

.checkbox-cell {
  width: 50px;
  text-align: center;
  vertical-align: middle;
  min-height: 120px;
}

.numbering-cell {
  width: 40px;
  text-align: center;
}

.risk-cell {
  width: 130px;
}

.empty-row {
  background-color: #fafafa;
}

.empty-cell {
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

/* Row and cell height adjustments */
.table-row {
  min-height: 120px;
}

.table-cell {
  position: relative;
  height: 120px;
}

/* Full height textarea styles */
.table-cell .ml-textarea,
.table-cell textarea {
  height: calc(100% - 14px) !important;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
}

/* Center checkbox vertically */
.checkbox-cell .vs-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.row-w1 {
  min-width: $w-inpu1;
}

.row-w0 {
  min-width: $w-inpu0;
}

.bx {
  border-left: $border;
  border-right: $border;
}

.br {
  border-right: $border;
}

.bl {
  border-left: $border;
}

.bb {
  border-bottom: $border;
}

.numbering {
  text-align: center;
  align-content: center;
  min-width: 40px;
}

/* Mobile Layout Styles */
@media (max-width: 768px) {
  .desktop-layout {
    display: none !important;
  }

  .mobile-layout {
    display: block !important;
  }

  .mobile-risk-item::v-deep {
    .con-select {
      width: inherit;
    }
  }
}

@media (min-width: 769px) {
  .mobile-layout {
    display: none !important;
  }

  .desktop-layout {
    display: flex !important;
  }
}

.mobile-layout {
  margin-top: 15px;
}

/* Mobile Title Header */
.mobile-title-header {
  margin-top: 0;
  margin-bottom: 15px;
}

.mobile-title-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px 0;
  flex-wrap: wrap;
  gap: 15px;
}

.mobile-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  flex: 1;
  min-width: 0;
}

.mobile-title .dot {
  margin-right: 8px;
}

/* Mobile AI Buttons Container */
.mobile-ai-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 200px;
  width: 100%;
  max-width: 250px;
}

/* Mobile Title Header에서 AI 버튼 스타일 */
.mobile-title-header .ai-button {
  flex-shrink: 0;
  width: 100% !important;
  justify-content: center;
}

/* 작은 화면에서는 AI 버튼을 전체 폭으로 */
@media (max-width: 480px) {
  .mobile-title-content {
    flex-direction: column;
    align-items: stretch;
  }

  .mobile-ai-buttons {
    max-width: none;
    width: 100%;
  }
}

/* Mobile Header */
.mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f9f9f9;
  border: $border;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
}

.mobile-select-all {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
}

.mobile-header-actions {
  display: flex;
  gap: 8px;
}

/* Mobile Cards Container */
.mobile-cards-container {
  border: $border;
  border-radius: 8px;
}

/* Mobile Empty State */
.mobile-empty-state {
  padding: 60px 20px;
  text-align: center;
  background-color: #fafafa;
  border-radius: 8px;
}

/* Mobile Risk Card */
.mobile-risk-card {
  border-bottom: $border;
  background-color: white;

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 8px 8px;
  }
}

.mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f9f9f9;
  border-bottom: $border;
}

.mobile-card-select {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-card-number {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.mobile-card-content {
  padding: 20px 15px;
}

/* Mobile Field Styles */
.mobile-field {
  margin-bottom: 25px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
}

.mobile-field-text {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  color: #495057;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
  min-height: 48px;
}

.mobile-textarea {
  width: 100% !important;
  min-height: 100px !important;
  resize: vertical;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 12px;

  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
}

/* Mobile Sub Field */
.mobile-sub-field {
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-sub-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin-bottom: 6px;
}

/* Mobile Risk Grid */
.mobile-risk-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 10px;
}

.mobile-risk-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-risk-result {
  grid-column: 1 / -1;
}

.mobile-risk-label {
  font-size: 13px;
  font-weight: 500;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-risk-value {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile Add Button */
.mobile-add-button {
  padding: 20px 15px;
  text-align: center;
  background-color: #f9f9f9;
  border: $border;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.mobile-add-btn {
  width: 100%;
  min-height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
}

/* Mobile Button Spacing */
.mobile-layout .vs-button {
  min-height: 44px;
  border-radius: 6px;
}

.mobile-layout .ai-button {
  width: 100%;
  justify-content: center;
}

/* Mobile Select and Input Styles */
.mobile-layout .vs-select,
.mobile-layout .ml-input {
  min-height: 48px;
  border-radius: 6px;

  .vs-select__input {
    font-size: 14px;
  }
}

.mobile-layout .vs-select-item {
  padding: 12px 16px;
  font-size: 14px;
}

/* Mobile vs-checkbox 스타일 개선 */
.mobile-layout .vs-checkbox {
  .vs-checkbox-con {
    width: 20px;
    height: 20px;
  }
}

/* Print Styles - Desktop Layout 강제 적용 */
@media print {
  .mobile-layout {
    display: none !important;
  }

  .desktop-layout {
    display: block !important;
  }
}
</style>
